// Phase 43.57 — Widget "Conversion this week" per admin dashboard.
// Mostra metriche funnel out-of-rides: viewed → click CTA → conversion %.
// Tre tab: Oggi · Ultimi 7 giorni · Totale.
import { useEffect, useState } from "react";
import { TrendingUp, Eye, Sparkles, Package, Zap, MousePointer } from "lucide-react";
import { api } from "@/lib/api";

const WINDOWS = [
  { key: "today", label: "Oggi" },
  { key: "last_7d", label: "Ultimi 7 giorni" },
  { key: "total", label: "Totale" },
];

export default function ConversionWidget() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("last_7d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    const load = () => {
      api.get("/admin/analytics/conversion")
        .then((r) => { if (on) setData(r.data); })
        .catch(() => { if (on) setData(null); })
        .finally(() => { if (on) setLoading(false); });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { on = false; clearInterval(t); };
  }, []);

  const bucket = data?.[tab];
  const empty = !loading && bucket && bucket.viewed === 0 && bucket.total_clicks === 0;

  return (
    <section className="card-soft p-5 sm:p-6" data-testid="admin-conversion-widget">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-dapnow-blue">Funnel · Corse esaurite</p>
          <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight mt-1">
            Conversion this week
          </h2>
          <p className="text-xs text-dapnow-muted mt-0.5">
            Quanti clienti vedono il blocco "corse terminate" e quanti cliccano per agire.
          </p>
        </div>
        <div className="inline-flex rounded-xl border border-dapnow-line bg-white p-0.5 text-xs">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setTab(w.key)}
              data-testid={`conversion-tab-${w.key}`}
              className={`rounded-lg px-3 py-1.5 font-semibold transition ${
                tab === w.key
                  ? "bg-dapnow-ink text-white"
                  : "text-dapnow-muted hover:text-dapnow-ink"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      {loading && (
        <p className="mt-6 text-sm text-dapnow-muted" data-testid="conversion-loading">Caricamento metriche…</p>
      )}

      {!loading && !bucket && (
        <p className="mt-6 text-sm text-dapnow-muted">Nessun dato disponibile.</p>
      )}

      {!loading && bucket && empty && (
        <div className="mt-6 rounded-xl bg-dapnow-bg border border-dapnow-line p-4 text-sm text-dapnow-muted text-center" data-testid="conversion-empty">
          Nessun evento registrato in questa finestra. Il widget si popola appena un cliente vede il blocco "Corse terminate".
        </div>
      )}

      {!loading && bucket && !empty && (
        <>
          {/* Hero row */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricTile
              icon={Eye}
              label="Visualizzazioni"
              value={bucket.viewed}
              sub={`${bucket.unique_users_viewed} client${bucket.unique_users_viewed === 1 ? "e" : "i"} unic${bucket.unique_users_viewed === 1 ? "o" : "i"}`}
              tone="default"
              testid="metric-viewed"
            />
            <MetricTile
              icon={MousePointer}
              label="Click totali"
              value={bucket.total_clicks}
              sub={`su ${bucket.viewed} visti`}
              tone="default"
              testid="metric-clicks"
            />
            <MetricTile
              icon={TrendingUp}
              label="Conversion rate"
              value={`${bucket.conversion_rate_pct.toFixed(1)}%`}
              sub={bucket.viewed > 0 ? "clicks / viewed" : "—"}
              tone="primary"
              testid="metric-conversion"
            />
            <MetricTile
              icon={Sparkles}
              label="Upgrade completati"
              value={bucket.upgrade_completed}
              sub="Stripe OK"
              tone="emerald"
              testid="metric-upgrade-completed"
            />
          </div>

          {/* CTA breakdown */}
          <div className="mt-5">
            <p className="overline text-[10px] mb-2">Click per CTA</p>
            <div className="space-y-2.5">
              <CtaBar
                icon={Sparkles}
                label="Upgrade piano"
                count={bucket.cta_upgrade}
                pct={bucket.ctr_upgrade_pct}
                viewed={bucket.viewed}
                color="#0052FF"
                testid="cta-bar-upgrade"
              />
              <CtaBar
                icon={Package}
                label="Pacchetto +10 corse"
                count={bucket.cta_pack}
                pct={bucket.ctr_pack_pct}
                viewed={bucket.viewed}
                color="#10B981"
                testid="cta-bar-pack"
              />
              <CtaBar
                icon={Zap}
                label="Corsa singola"
                count={bucket.cta_single}
                pct={bucket.ctr_single_pct}
                viewed={bucket.viewed}
                color="#9CA3AF"
                testid="cta-bar-single"
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function MetricTile({ icon: Icon, label, value, sub, tone, testid }) {
  const tones = {
    default: "border-dapnow-line bg-white",
    primary: "border-dapnow-blue/30 bg-dapnow-blue-soft/40 text-dapnow-ink",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.default}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5 opacity-70" />
        <p className="overline text-[10px] truncate">{label}</p>
      </div>
      <p className="font-display text-xl sm:text-2xl font-extrabold tabular-nums truncate" title={String(value)}>
        {value}
      </p>
      <p className="text-[11px] mt-0.5 opacity-70 truncate">{sub}</p>
    </div>
  );
}

function CtaBar({ icon: Icon, label, count, pct, viewed, color, testid }) {
  const width = viewed > 0 ? Math.min(100, pct) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 text-xs mb-1">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <span className="font-semibold text-dapnow-ink">{label}</span>
        <span className="ml-auto tabular-nums font-mono text-dapnow-muted">
          <strong className="text-dapnow-ink">{count}</strong> · {pct.toFixed(1)}% CTR
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-dapnow-line overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${width}%`, background: color }} />
      </div>
    </div>
  );
}
